import type { ComponentProps } from "react";

import type { PresentationReviewResponse } from "@/api/types";
import { SafeMarkdown } from "@/components/SafeMarkdown";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type Recommendations = PresentationReviewResponse["recommendations"];
type RecommendedTool = Recommendations["tools"][number];

/**
 * Renders the Analyst + Match Validator's output for the presentation
 * review surface (issue #144).
 *
 * XSS posture: only two strings on this component are rendered via
 * markdown — `custom_proposal_narrative` and each tool's `rationale`.
 * Both are LLM-produced with strict length bounds
 * (`custom_proposal_narrative`: 200–20 000 chars; `rationale`: 20–2000
 * chars) and pass through {@link SafeMarkdown}, which sets
 * `skipHtml` + `disallowedElements` per the
 * `docs/decisions/2026-06-15-admin-ui-xss-safe-rendering.md` ADR.
 * Every other string (removal_reason, ranking_order_assessment
 * reason) stays on plain React text-interpolation.
 *
 * Three solution-type discriminants are handled:
 * - `tool_assembly` — pure tool list, `custom_proposal_narrative` null.
 * - `custom_build` — narrative + up to 3 tools.
 * - `hybrid` — narrative + up to 4 tools (forward-compat slot).
 *
 * Metrics behaviour (issue #575, finding #4): `custom_build` has no
 * shortlist to score against and no assembled objectives-coverage to
 * sum, so Coverage hides and the Match-Validator Score renders as
 * `Score N/A` with a hover tooltip explaining why. `tool_assembly`
 * and `hybrid` show both metrics as percentages.
 *
 * Tools with `match_validation_status === "removed"` render with
 * strikethrough + a "removed" badge + the validator's reason, but
 * stay in the list so the reviewer can see the full Analyst case.
 */
export interface RecommendationsDisplayProps {
  recommendations: Recommendations;
  matchValidationScore: number | null;
  matchValidationStatus: PresentationReviewResponse["matchValidationStatus"];
}

const CUSTOM_BUILD_METRICS_TOOLTIP =
  "Coverage and Match-Validator score do not apply to a custom build — there is no catalogue shortlist to score against and no assembled objectives-coverage to sum.";

export function RecommendationsDisplay({
  recommendations,
  matchValidationScore,
  matchValidationStatus,
}: RecommendationsDisplayProps) {
  const isCustomBuild = recommendations.solution_type === "custom_build";
  const coveragePct = Math.round(
    recommendations.objectives_coverage_pct * 100,
  );
  const scorePct =
    matchValidationScore !== null
      ? Math.round(matchValidationScore * 100)
      : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>Recommendations</CardTitle>
            <CardDescription>
              {labelForSolutionType(recommendations.solution_type)} ·{" "}
              {recommendations.tools.length}{" "}
              {recommendations.tools.length === 1 ? "tool" : "tools"}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant={matchValidationStatus === "flagged" ? "danger" : "default"}>
              {matchValidationStatus}
            </Badge>
            <div
              className="text-xs text-muted-foreground"
              title={isCustomBuild ? CUSTOM_BUILD_METRICS_TOOLTIP : undefined}
            >
              {isCustomBuild ? (
                <>Coverage N/A · Score N/A</>
              ) : (
                <>
                  Coverage {coveragePct}%
                  {scorePct !== null && <> · Score {scorePct}%</>}
                </>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {recommendations.ranking_order_assessment && (
          <RankingAssessment
            assessment={recommendations.ranking_order_assessment}
          />
        )}

        {recommendations.custom_proposal_narrative && (
          <CustomNarrative
            narrative={recommendations.custom_proposal_narrative}
          />
        )}

        {recommendations.tools.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Tools</h3>
            <div className="space-y-3">
              {recommendations.tools.map((tool) => (
                <ToolCard key={tool.toolId} tool={tool} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function labelForSolutionType(t: Recommendations["solution_type"]): string {
  switch (t) {
    case "tool_assembly":
      return "Tool assembly";
    case "custom_build":
      return "Custom build";
    case "hybrid":
      return "Hybrid";
  }
}

interface RankingAssessmentProps {
  assessment: NonNullable<Recommendations["ranking_order_assessment"]>;
}

function RankingAssessment({ assessment }: RankingAssessmentProps) {
  // Palette toggles on the verdict so an "OK" banner does not carry the
  // amber warning weight (issue #616 — surfaced in the Wave B smoke walk
  // where a passed ranking still read as a flagged one). The passed
  // branch uses the admin-ui's neutral theme tokens (`bg-bg-card` +
  // `text-text-muted`) so it blends with the dark surface instead of
  // reading as a bright rectangle (issue #630 — Wave C smoke walk). The
  // flagged branch uses the established amber-400/opacity pattern shared
  // with `ClearOrphanDraftsBanner`, `JobDetail`'s stall banner, and
  // `ManualRun`'s flagged-memo notice.
  const palette = assessment.passed
    ? { container: "border-border bg-bg-card", text: "text-text-muted" }
    : {
        container: "border-amber-400/40 bg-amber-400/10",
        text: "text-amber-300",
      };
  return (
    <div className={`rounded-md border p-3 ${palette.container}`}>
      <div
        className={`text-xs font-medium uppercase tracking-wider ${palette.text}`}
      >
        Ranking order · {assessment.passed ? "OK" : "Flagged"}
      </div>
      {assessment.reason && (
        <div className={`mt-1 text-sm ${palette.text}`}>{assessment.reason}</div>
      )}
    </div>
  );
}

/**
 * Per-element component overrides passed to `react-markdown` so
 * headings / lists / paragraphs in the Analyst's markdown inherit
 * the admin surface's typography without pulling in the
 * `@tailwindcss/typography` plugin. Shared between the custom
 * narrative and the per-tool rationale so both render with
 * consistent spacing.
 */
const MARKDOWN_COMPONENTS: NonNullable<
  ComponentProps<typeof SafeMarkdown>["components"]
> = {
  h1: (props) => <h4 className="mt-3 mb-1 text-base font-semibold" {...props} />,
  h2: (props) => <h5 className="mt-3 mb-1 text-sm font-semibold" {...props} />,
  h3: (props) => <h6 className="mt-3 mb-1 text-sm font-semibold" {...props} />,
  p: (props) => <p className="my-2" {...props} />,
  ul: (props) => <ul className="my-2 list-disc pl-5 space-y-1" {...props} />,
  ol: (props) => <ol className="my-2 list-decimal pl-5 space-y-1" {...props} />,
  li: (props) => <li className="my-0" {...props} />,
  a: (props) => (
    // `rel`/`target` are set BEFORE the `{...props}` spread so a
    // future refactor that reorders these can't have the LLM-produced
    // markdown override the safe-link defaults. `href` and `children`
    // are the only meaningful props react-markdown emits for anchors.
    <a
      className="underline text-blue-600 hover:text-blue-800"
      rel="noopener noreferrer"
      target="_blank"
      {...props}
    />
  ),
  code: (props) => (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" {...props} />
  ),
};

function CustomNarrative({ narrative }: { narrative: string }) {
  return (
    <div>
      <h3 className="text-sm font-semibold">Custom narrative</h3>
      <SafeMarkdown
        className="mt-2 rounded-md bg-muted/50 p-3 text-sm"
        components={MARKDOWN_COMPONENTS}
      >
        {narrative}
      </SafeMarkdown>
    </div>
  );
}

interface ToolCardProps {
  tool: RecommendedTool;
}

function ToolCard({ tool }: ToolCardProps) {
  const removed = tool.match_validation_status === "removed";
  const scorePctOf5 =
    tool.match_validation_score !== undefined
      ? Math.round((tool.match_validation_score / 5) * 100)
      : null;

  return (
    <div
      className={`rounded-md border p-3 ${
        removed ? "border-red-200 bg-red-50/40" : "border-border bg-card"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-semibold ${removed ? "line-through text-muted-foreground" : ""}`}
          >
            {tool.name}
          </div>
          <div className="text-xs text-muted-foreground">{tool.slug}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {removed && <Badge variant="danger">removed</Badge>}
          {!removed && tool.match_validation_status === "kept" && (
            <Badge variant="default">kept</Badge>
          )}
          {scorePctOf5 !== null && (
            <div className="text-xs text-muted-foreground">
              MV {tool.match_validation_score?.toFixed(1)}/5
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Obj {Math.round(tool.objective_score * 100)}%
          </div>
        </div>
      </div>

      {removed && tool.removal_reason && (
        <div className="mt-2 rounded bg-red-100 px-2 py-1 text-xs text-red-900">
          <span className="font-medium">Removed:</span> {tool.removal_reason}
        </div>
      )}

      <SafeMarkdown className="mt-2 text-sm" components={MARKDOWN_COMPONENTS}>
        {tool.rationale}
      </SafeMarkdown>

      {tool.match_check_outcomes && tool.match_check_outcomes.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            Validator checks ({tool.match_check_outcomes.length})
          </summary>
          <ul className="mt-1 space-y-1 text-xs">
            {tool.match_check_outcomes.map((outcome, idx) => (
              <li
                key={`${tool.toolId}-${outcome.checkId}-${idx}`}
                className="flex items-baseline gap-2"
              >
                <span
                  className={
                    outcome.passed ? "text-green-700" : "text-red-700"
                  }
                >
                  {outcome.passed ? "✓" : "✗"}
                </span>
                <span className="font-mono text-muted-foreground">
                  {outcome.checkId}
                </span>
                {outcome.reason && <span>{outcome.reason}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
